import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { listAll, saveSingleton } from "@/lib/adminRepository";
import { ADMIN } from "@/constants/testIds";

// Per-page SEO meta editor (MeProperty-style), themed for A Homely Abode.
// Each page maps to a `seo/{key}` doc. The public site reads these via
// useSeo() / publicSettings.js — keep this list in sync with PATH_SCOPE there.
export const SEO_PAGES = [
  { key: "home", label: "Home", path: "/" },
  { key: "about", label: "About Me", path: "/why-me/about-me" },
  { key: "services", label: "Services", path: "/why-me/services" },
  { key: "framework", label: "Framework", path: "/framework" },
  { key: "listings", label: "Listings", path: "/listings" },
  { key: "proven-results", label: "Proven Results", path: "/success-stories/proven-results" },
  { key: "testimonials", label: "Testimonials", path: "/success-stories/testimonials" },
  { key: "awards", label: "Awards", path: "/success-stories/awards" },
  { key: "calculators", label: "Calculators", path: "/resources/calculators" },
  { key: "property-valuation", label: "Property Valuation", path: "/resources/property-valuation" },
  { key: "blogs", label: "Blogs", path: "/resources/blogs" },
  { key: "contact", label: "Contact", path: "/contact" },
];

const BLANK = { title: "", description: "", keywords: "" };

// Keep only the editable meta fields (drop id/timestamps); strip empty strings
// so a blank field never overrides the global default on the public site.
function cleanDraft(draft) {
  const out = {};
  for (const k of ["title", "description", "keywords"]) {
    const v = (draft[k] || "").trim();
    if (v) out[k] = v;
  }
  return out;
}

export default function SeoSettings() {
  const [active, setActive] = useState("home");
  const [docs, setDocs] = useState({}); // key -> seo data
  const [draft, setDraft] = useState(BLANK);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = await listAll("seo"); // [{ id, ...data }]
        if (cancelled) return;
        const map = {};
        rows.forEach((r) => { map[r.id] = r; });
        setDocs(map);
        setDraft({ ...BLANK, ...(map.home || {}) });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[seo] load failed:", err);
        toast.error("Could not load SEO settings.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Imperative tab switch (no effect on `docs`) — avoids the save→reload race
  // that would otherwise wipe the success toast.
  const selectPage = (key) => {
    setActive(key);
    setDraft({ ...BLANK, ...(docs[key] || {}) });
  };

  const update = (k) => (e) => setDraft((d) => ({ ...d, [k]: e.target.value }));

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = cleanDraft(draft);
      await saveSingleton("seo", active, payload);
      setDocs((prev) => ({ ...prev, [active]: { id: active, ...payload } }));
      toast.success(`Saved SEO for “${SEO_PAGES.find((p) => p.key === active)?.label}”.`);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[seo] save failed:", err);
      toast.error("Save failed: " + (err?.message || "unknown error"));
    } finally {
      setSaving(false);
    }
  };

  const activePage = SEO_PAGES.find((p) => p.key === active);
  const isConfigured = (key) => !!(docs[key]?.title || docs[key]?.description || docs[key]?.keywords);

  return (
    <div data-testid="admin-seo">
      <header className="mb-8">
        <div className="eyebrow text-bronze mb-2">Site</div>
        <h1 className="font-serif text-3xl md:text-4xl text-ink">SEO</h1>
        <p className="text-sm text-olive mt-2 max-w-xl">
          Per-page meta tags — title, description and keywords. Read live by the public
          site on each route, overriding the built-in defaults.
        </p>
      </header>

      <div className="grid lg:grid-cols-[230px,1fr] gap-6">
        {/* Page picker */}
        <nav className="border border-bronze/20 bg-cream rounded-none p-2 self-start" data-testid="admin-seo-pages">
          {SEO_PAGES.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => selectPage(p.key)}
              data-testid={`admin-seo-tab-${p.key}`}
              className={`w-full text-left px-3 py-2.5 text-sm transition-colors flex items-center justify-between ${
                active === p.key ? "bg-bronze text-cream" : "text-ink_soft hover:bg-beige/60"
              }`}
            >
              {p.label}
              {isConfigured(p.key) && (
                <span
                  className={`h-1.5 w-1.5 rounded-full ${active === p.key ? "bg-cream" : "bg-bronze"}`}
                  title="Configured"
                />
              )}
            </button>
          ))}
        </nav>

        {/* Editor */}
        <form onSubmit={handleSave} className="border border-bronze/20 bg-cream p-6 md:p-8 space-y-6" data-testid={ADMIN.seoForm}>
          <div className="text-[0.62rem] tracking-[0.25em] uppercase text-olive">
            {activePage?.path}
          </div>

          {loading ? (
            <p className="text-olive text-sm">Loading…</p>
          ) : (
            <>
              <label className="block">
                <span className="editorial-label">Meta title</span>
                <input
                  className="editorial-input"
                  value={draft.title || ""}
                  onChange={update("title")}
                  data-testid="admin-seo-title"
                  placeholder="Shown in the browser tab + search results (aim 50–60 chars)"
                />
              </label>

              <label className="block">
                <span className="editorial-label">Meta description</span>
                <textarea
                  rows={3}
                  className="editorial-input resize-y"
                  value={draft.description || ""}
                  onChange={update("description")}
                  data-testid="admin-seo-desc"
                  placeholder="Shown under the title in search results (aim 150–160 chars)"
                />
              </label>

              <label className="block">
                <span className="editorial-label">Keywords</span>
                <input
                  className="editorial-input"
                  value={draft.keywords || ""}
                  onChange={update("keywords")}
                  data-testid="admin-seo-keywords"
                  placeholder="Comma-separated. Weighted lightly by modern search engines."
                />
              </label>
            </>
          )}

          <div className="flex justify-end pt-2">
            <button
              type="submit"
              disabled={saving || loading}
              data-testid="admin-seo-save"
              className="btn-primary disabled:opacity-60"
            >
              <Save size={14} /> {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
